import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Brain, 
  TrendingUp, 
  Package, 
  AlertTriangle, 
  CheckCircle, 
  DollarSign, 
  MessageSquare, 
  Sliders, 
  ArrowUpRight 
} from 'lucide-react';
import { toast } from 'sonner';

import API from '../api_config';

const formatCLP = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
};

const PriorityBadge = ({ priority }) => {
  const colors = {
    critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border border-orange-200 dark:border-orange-850',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800',
    low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800'
  };

  const labels = {
    critical: 'Crítico',
    high: 'Alto',
    medium: 'Medio',
    low: 'Bajo'
  };

  return (
    <span className={`px-2.5 py-0.5 text-xs rounded-full font-medium ${colors[priority] || colors.medium}`}>
      {labels[priority] || priority}
    </span>
  );
};

export default function AIPredictionsPage() {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'reorder', 'price_increase', 'debt_collection', 'expense_control'
  const navigate = useNavigate();

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/ai/recommendations`);
      setRecommendations(response.data);
    } catch (error) {
      toast.error('Error al cargar recomendaciones financieras');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getWhatsAppLink = (phone, balance, title) => {
    const cleanPhone = phone ? phone.replace(/[^\d]/g, '') : '';
    const customerName = title.replace('Recordatorio de pago para ', '').replace('Recordatorio de pago: ', '');
    const msg = `Hola ${customerName}, le saludamos de GKServicePOS. Le recordamos amablemente que cuenta con un saldo pendiente de ${formatCLP(balance)}. ¿Nos podría confirmar si necesita los datos de transferencia para el abono? ¡Muchas gracias!`;
    return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`;
  };

  const filteredRecommendations = recommendations.filter(rec => {
    if (filter === 'all') return true;
    return rec.type === filter;
  });

  const criticalCount = recommendations.filter(r => r.priority === 'critical').length;
  const highCount = recommendations.filter(r => r.priority === 'high').length;
  const reorderCount = recommendations.filter(r => r.type === 'reorder').length;
  const debtCount = recommendations.filter(r => r.type === 'debt_collection').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center space-y-3">
          <Brain className="h-10 w-10 text-indigo-600 animate-pulse mx-auto" />
          <div className="text-muted-foreground font-medium">Ejecutando algoritmos financieros de IA...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-predictions-page">
      <div className="flex items-center space-x-3">
        <div className="p-3 bg-indigo-600 rounded-2xl shadow-indigo-200 dark:shadow-none shadow-md">
          <Brain className="h-8 w-8 text-white" />
        </div>
        <div>
          <h1 className="text-4xl font-bold tracking-tight">POS Inteligente Financiero</h1>
          <p className="text-muted-foreground mt-1">
            Recomendaciones inteligentes y acciones automatizadas para optimizar tu negocio
          </p>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="hover:shadow-md transition-shadow border-red-100 dark:border-red-950">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Alertas Críticas
            </CardTitle>
            <AlertTriangle className="h-5 w-5 text-red-600 animate-bounce" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600 dark:text-red-400">{criticalCount}</div>
            <p className="text-xs text-muted-foreground mt-1">Requieren acción inmediata</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow border-orange-100 dark:border-orange-950">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Prioridad Alta
            </CardTitle>
            <TrendingUp className="h-5 w-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">{highCount}</div>
            <p className="text-xs text-muted-foreground mt-1">Sugerido abordar hoy</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Compras Sugeridas
            </CardTitle>
            <Package className="h-5 w-5 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{reorderCount}</div>
            <p className="text-xs text-muted-foreground mt-1">Productos bajo stock mínimo</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Cobros Pendientes
            </CardTitle>
            <MessageSquare className="h-5 w-5 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{debtCount}</div>
            <p className="text-xs text-muted-foreground mt-1 font-mono">Clientes con cuentas inactivas</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-4">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')}
          size="sm"
          data-testid="filter-all"
          className="rounded-xl"
        >
          Todas ({recommendations.length})
        </Button>
        <Button
          variant={filter === 'reorder' ? 'default' : 'outline'}
          onClick={() => setFilter('reorder')}
          size="sm"
          className="rounded-xl"
        >
          <Package className="h-4 w-4 mr-2" />
          Reabastecimiento ({reorderCount})
        </Button>
        <Button
          variant={filter === 'price_increase' ? 'default' : 'outline'}
          onClick={() => setFilter('price_increase')}
          size="sm"
          className="rounded-xl"
        >
          <TrendingUp className="h-4 w-4 mr-2" />
          Márgenes y Precios ({recommendations.filter(r => r.type === 'price_increase').length})
        </Button>
        <Button
          variant={filter === 'debt_collection' ? 'default' : 'outline'}
          onClick={() => setFilter('debt_collection')}
          size="sm"
          className="rounded-xl"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Cobranzas ({debtCount})
        </Button>
        <Button
          variant={filter === 'expense_control' ? 'default' : 'outline'}
          onClick={() => setFilter('expense_control')}
          size="sm"
          className="rounded-xl"
        >
          <Sliders className="h-4 w-4 mr-2" />
          Gastos ({recommendations.filter(r => r.type === 'expense_control').length})
        </Button>
      </div>

      {/* Recommendations Cards */}
      <div className="grid grid-cols-1 gap-4">
        {filteredRecommendations.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="text-center py-16 text-muted-foreground">
              <CheckCircle className="h-14 w-14 mx-auto mb-4 text-emerald-500 opacity-80" />
              <h3 className="text-lg font-semibold text-foreground">¡Todo en orden!</h3>
              <p className="mt-1 text-sm">No se encontraron recomendaciones inteligentes para esta sección.</p>
            </CardContent>
          </Card>
        ) : (
          filteredRecommendations.map((rec) => {
            // Pick layout details based on type
            let typeColor = "indigo";
            let typeIcon = <Brain className="h-6 w-6 text-indigo-600" />;
            let detailsView = null;
            let actionRoute = rec.action_url;

            if (rec.type === 'reorder') {
              typeColor = "blue";
              typeIcon = <Package className="h-6 w-6 text-blue-600" />;
              detailsView = rec.metadata.recommended_qty ? (
                <div className="mt-3 inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 px-3 py-1.5 rounded-lg text-sm font-semibold">
                  <span>Cantidad Sugerida:</span>
                  <span className="font-mono text-base font-bold bg-blue-100 dark:bg-blue-800 px-2 py-0.5 rounded text-blue-900 dark:text-blue-200">
                    {rec.metadata.recommended_qty} uds
                  </span>
                </div>
              ) : null;
            } else if (rec.type === 'price_increase') {
              typeColor = "amber";
              typeIcon = <TrendingUp className="h-6 w-6 text-amber-600" />;
              detailsView = (
                <div className="mt-3 flex flex-wrap gap-4 items-center bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg text-sm text-amber-900 dark:text-amber-300">
                  <div>
                    <span className="text-muted-foreground mr-1 text-xs uppercase tracking-wider block">Precio Actual</span>
                    <span className="font-mono font-bold text-base line-through opacity-70">{formatCLP(rec.metadata.current_price)}</span>
                  </div>
                  <div className="text-amber-500 font-bold text-lg">→</div>
                  <div>
                    <span className="text-amber-800 dark:text-amber-400 mr-1 text-xs uppercase tracking-wider block font-semibold">Sugerido (Neto + IVA)</span>
                    <span className="font-mono font-extrabold text-base bg-amber-200 dark:bg-amber-800/80 px-2 py-1 rounded text-amber-950 dark:text-amber-100">
                      {formatCLP(rec.metadata.suggested_price)}
                    </span>
                  </div>
                </div>
              );
            } else if (rec.type === 'debt_collection') {
              typeColor = "emerald";
              typeIcon = <MessageSquare className="h-6 w-6 text-emerald-600" />;
              detailsView = (
                <div className="mt-3 flex flex-col md:flex-row gap-4 items-start md:items-center bg-emerald-50 dark:bg-emerald-950/20 px-3 py-2.5 rounded-lg text-sm text-emerald-900 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900/50">
                  <div>
                    <span className="text-muted-foreground mr-1 text-xs uppercase tracking-wider block">Monto Adeudado</span>
                    <span className="font-mono font-extrabold text-lg text-emerald-700 dark:text-emerald-400">{formatCLP(rec.metadata.balance)}</span>
                  </div>
                  {rec.metadata.phone && (
                    <div className="md:ml-auto">
                      <Button
                        size="sm"
                        variant="default"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm"
                        onClick={() => window.open(getWhatsAppLink(rec.metadata.phone, rec.metadata.balance, rec.title), '_blank')}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Enviar Recordatorio WhatsApp
                      </Button>
                    </div>
                  )}
                </div>
              );
            } else if (rec.type === 'expense_control') {
              typeColor = "purple";
              typeIcon = <Sliders className="h-6 w-6 text-purple-600" />;
              detailsView = rec.metadata.increase_pct ? (
                <div className="mt-3 inline-flex items-center gap-2 bg-purple-50 dark:bg-purple-950/20 text-purple-800 dark:text-purple-300 px-3 py-1.5 rounded-lg text-sm font-semibold border border-purple-100 dark:border-purple-900/50">
                  <span>Incremento:</span>
                  <span className="font-mono text-base font-extrabold text-red-650 bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded text-red-700 dark:text-red-400">
                    +{roundDec(rec.metadata.increase_pct, 1)}%
                  </span>
                </div>
              ) : null;
            }

            return (
              <Card 
                key={rec.id}
                className="hover:shadow-md transition-all border-l-4 border-l-indigo-500 hover:border-l-indigo-600 duration-200"
                style={{ borderLeftColor: `var(--color-${typeColor})`, '--color-blue': '#2563eb', '--color-amber': '#d97706', '--color-emerald': '#059669', '--color-purple': '#7c3aed', '--color-indigo': '#4f46e5' }}
                data-testid={`recommendation-card-${rec.type}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="p-3 bg-muted rounded-xl flex-shrink-0">
                      {typeIcon}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <h3 className="font-bold text-lg text-foreground leading-tight">{rec.title}</h3>
                        <PriorityBadge priority={rec.priority} />
                      </div>
                      <p className="text-muted-foreground text-sm leading-relaxed">{rec.message}</p>
                      
                      {detailsView}
                    </div>
                    
                    <div className="flex-shrink-0 self-center">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-xl hover:bg-muted font-medium"
                        onClick={() => navigate(actionRoute)}
                      >
                        {rec.action_text}
                        <ArrowUpRight className="h-4 w-4 ml-1.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}

function roundDec(num, decimales = 1) {
  return Math.round(num * Math.pow(10, decimales)) / Math.pow(10, decimales);
}